import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  Button,
  csx,
  FilterFooter,
  FilterListbox,
  FilterOptionCheckbox,
  FilterPopover,
  FilterSearchbox,
  Skeleton,
  Stack,
  useDebounce,
  type UseFilterMultipleReturn,
} from '@vtex/admin-ui'
import { BaseFilter } from '@/components/filters/BaseFilter/BaseFilter'
import { useFilterMultiple } from '@/components/filters/hooks/useFilterMultiple'
import { type Brand, useBrands } from '@/hooks/useBrands'

const SIZE = 10

function FilterBrandPopover<T = Brand>(props: FilterBrandProps<T>) {
  const { formatMessage } = useIntl()
  const {
    items,
    paginate,
    hasMoreItems,
    pagination,
    state,
    visible,
    loadingSearch,
  } = useBrandFilter(props)

  return (
    <FilterPopover state={state}>
      <FilterSearchbox loading={loadingSearch} />
      <FilterListbox>
        {visible
          ? items.map((brand) => (
              <FilterOptionCheckbox
                key={String(brand.id)}
                id={String(brand.id)}
                label={brand.name}
              />
            ))
          : null}
        {pagination.loading ? <BrandPaginationSkeleton /> : null}
        {hasMoreItems ? (
          <Button
            onClick={() => {
              paginate(items[items.length - 1])
            }}
            variant="neutralTertiary"
            className={csx({ width: '100%' })}
          >
            {formatMessage({
              id: 'admin/admin.catalog.brand-filter-pagination-label',
              defaultMessage: 'View more',
            })}
          </Button>
        ) : null}
      </FilterListbox>
      <FilterFooter />
    </FilterPopover>
  )
}

export type SerializedData = string[]

export function FilterBrand(props: { name: string }) {
  const { formatMessage } = useIntl()

  return (
    <BaseFilter
      filterName={props.name}
      useFilterState={useFilterMultiple}
      FilterPopoverComponent={FilterBrandPopover}
      disclosureMessage={formatMessage({
        id: 'admin/admin.catalog.brand-filter-label',
        defaultMessage: 'Brand',
      })}
      toSerializableData={(data) =>
        (data.appliedItems?.map((item) => item.id) ?? []) as SerializedData
      }
    />
  )
}

function useBrandFilter<T>(props: FilterBrandProps<T>) {
  const { state } = props
  const [brandName] = useDebounce(state.combobox.value, 1000)

  const [loadingSearch, setLoadingSearch] = useState(false)
  const [moreItems, setMoreItems] = useState(true)
  const [pagination, setPagination] = useState({ query: '', loading: false })

  const { data: brands = [], isLoading } = useBrands({
    search: brandName ? `search=${brandName}` : '',
    pagination: pagination.query,
    size: `size=${SIZE}`,
  })

  const [items, setItems] = useState<Brand[]>(brands)

  useEffect(() => {
    const isPaginating = pagination.loading && pagination.query !== ''

    const isEmpty =
      items.length === 0 && brands.length === 0 && brandName.length === 0

    const isFirstLoading = isLoading && isEmpty && !isPaginating

    const isNotFound =
      brands.length === 0 && brandName.length > 0 && !isPaginating

    const hasMoreItems = brands.length >= SIZE

    if (isFirstLoading) {
      state.setStatus('loading')

      return
    }

    if (isLoading) return setLoadingSearch(true)

    setLoadingSearch(false)

    if (isNotFound) {
      state.setStatus('not-found')

      return
    }

    if (isEmpty) {
      state.setStatus('empty')

      return
    }

    if (isPaginating) {
      setItems((prev) => [...prev, ...brands])

      setPagination((prev) => ({ ...prev, loading: false }))
    } else if (brands.length > 0) {
      setItems(brands)
    }

    setMoreItems(hasMoreItems)

    state.setStatus('ready')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands, isLoading, brandName])

  useEffect(() => {
    setPagination({ query: '', loading: false })
  }, [brandName])

  function paginate(item: Brand) {
    if (!item) return

    setPagination({
      query: `after=${item.id}&brandName=${item.name}`,
      loading: true,
    })
  }

  return {
    items,
    paginate,
    pagination,
    hasMoreItems: moreItems,
    state,
    visible: state.menu.mounted,
    loadingSearch,
  }
}

function BrandPaginationSkeleton() {
  const skeletonElements = Array(SIZE).fill(0)

  return (
    <>
      {skeletonElements.map((_, index) => {
        return (
          <Stack
            direction="row"
            space="$space-3"
            key={`brand-pagination-skeleton-${index}`}
          >
            <div
              className={csx({
                size: 20,
                border: '$form.neutral',
                borderRadius: '$default',
              })}
            />
            <Skeleton
              className={csx({ height: 20, width: index % 2 === 0 ? 80 : 60 })}
            />
          </Stack>
        )
      })}
    </>
  )
}

interface FilterBrandProps<T> {
  state: UseFilterMultipleReturn<T>
}
